import { Modal } from "@circle-react-uikit/ModalV2";
import { Typography } from "@circle-react-uikit/Typography";
import { usePunditUserContext } from "@circle-react/contexts/punditUserContext";
import { usePlanStatus } from "@circle-react/hooks/usePlanStatus";
import { useEffect, useEffectEvent } from "react";

import { t } from "@/i18n-js/instance";
import { isDowngrading } from "@/react/helpers/planHelpers";

import { ContactSupport } from "../ContactSupport";
import { useCanDowngrade } from "../hooks/useCanDowngrade";
import { Loader } from "../Loader";
import type { UpgradePlan } from "../types";
import { UpgradePlanModalBody } from "../UpgradePlanModalBody";
import { UpgradePlanModalHeader } from "../UpgradePlanModalHeader";
import { RestrictionWrapper } from "./RestrictionWrapper";

interface UpgradeErrorModalComponentProps {
  changePlan: (plan: UpgradePlan) => void;
  hideModal: () => void;
  plan: UpgradePlan;
}

const i18nRoot = "pricing_plans.upgrade_modal";

export const CanDowngrade = ({
  plan,
  changePlan,
  hideModal,
}: UpgradeErrorModalComponentProps) => {
  const { currentPlanTier } = usePlanStatus();
  const isDowngradingThePlan =
    currentPlanTier && isDowngrading(currentPlanTier, plan.tier);
  const { currentCommunity } = usePunditUserContext();
  const isSubscriptionCancelled = currentCommunity?.subscription_cancelled;

  const { data, error, isPending, isSuccess } = useCanDowngrade(plan.id);
  const canDowngrade = isSuccess && Boolean(data?.can_downgrade);
  const changeToPlan = useEffectEvent(() => {
    changePlan(plan);
  });

  useEffect(() => {
    if (canDowngrade) changeToPlan();
  }, [canDowngrade]);

  if (isPending) {
    return <Loader title={t([i18nRoot, "loading.validating_limits"])} />;
  }
  if (error) {
    return (
      <ContactSupport
        title={t(
          "pricing_plans.upgrade_modal.errors.limits_validation_error.title",
        )}
        errorMessage={t(
          "pricing_plans.upgrade_modal.errors.limits_validation_error.message",
        )}
      />
    );
  }

  const actionType = isDowngradingThePlan ? "downgrade" : "upgrade";

  return (
    <>
      {data && !data.can_downgrade && (
        <>
          <UpgradePlanModalHeader
            title={t(
              isSubscriptionCancelled
                ? "plan_change.restrictions.plain_text.title_for_canceled_sub"
                : `plan_change.restrictions.plain_text.title_${actionType}`,
            )}
          >
            <Modal.Title>
              {t(
                isSubscriptionCancelled
                  ? "plan_change.restrictions.plain_text.title_for_canceled_sub"
                  : `plan_change.restrictions.plain_text.title_${actionType}`,
              )}
            </Modal.Title>
            <Modal.HeaderActions>
              <Modal.CloseButton onClick={hideModal} />
            </Modal.HeaderActions>
          </UpgradePlanModalHeader>
          <UpgradePlanModalBody className="mb-10 flex flex-col gap-y-2">
            {data.blocking_features && data.blocking_features.length === 0 ? (
              <Typography.BodyMd>
                {t(
                  "pricing_plans.upgrade_modal.errors.downgrade_check_error.message",
                )}
              </Typography.BodyMd>
            ) : (
              <>
                <Typography.BodyMd>
                  {t(
                    "plan_change.restrictions.plain_text.features_not_allowed",
                    {
                      plan: plan?.display_name,
                    },
                  )}
                </Typography.BodyMd>

                {!isSubscriptionCancelled && (
                  <Typography.BodyMd>
                    {t(
                      `plan_change.restrictions.plain_text.adjustments_needed.${actionType}`,
                    )}
                  </Typography.BodyMd>
                )}

                <RestrictionWrapper features={data.blocking_features} />

                {!isSubscriptionCancelled && (
                  <Typography.BodyMd>
                    {t("plan_change.restrictions.plain_text.do_not_care")}
                  </Typography.BodyMd>
                )}
              </>
            )}
          </UpgradePlanModalBody>
        </>
      )}
    </>
  );
};
